/**
 * Availability helpers — pure functions, used by every surface that needs to
 * show "when is this candidate available?".
 *
 * Sources (in this priority order):
 *   1. application.possible_joining_date  — recruiter-confirmed target for THIS role
 *   2. candidate.screening_profile.earliest_join_date
 *   3. today + candidate.screening_profile.notice_period_days
 *
 * If `on_notice_period === "Yes"` and notice_period_days is set, we use the
 * notice-based calculation; otherwise we treat the candidate as "available now"
 * (subject to whatever earliest_join_date / possible_joining_date says).
 */

const DAY_MS = 86_400_000;

function parseISODate(s) {
    if (!s) return null;
    // Accept YYYY-MM-DD or full ISO
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) {
        const d = new Date(s);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
}

function todayUTC() {
    const d = new Date();
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/**
 * Returns the candidate's effective availability date for an application.
 * @param {object} candidate - usually a candidate dict (may contain screening_profile)
 * @param {object} [application]
 * @returns {{ date: Date|null, daysOut: number|null, source: string|null }}
 */
export function computeAvailability(candidate, application) {
    const sp = candidate?.screening_profile || {};
    const candidates = [];

    if (application?.possible_joining_date) {
        const d = parseISODate(application.possible_joining_date);
        if (d) candidates.push({ date: d, source: "application_target" });
    }
    if (sp.earliest_join_date) {
        const d = parseISODate(sp.earliest_join_date);
        if (d) candidates.push({ date: d, source: "earliest_join_date" });
    }
    const days = Number(sp.notice_period_days);
    if (sp.on_notice_period === "Yes" && Number.isFinite(days) && days >= 0) {
        const d = new Date(todayUTC().getTime() + days * DAY_MS);
        candidates.push({ date: d, source: "notice_period" });
    }

    if (candidates.length === 0) {
        return { date: null, daysOut: null, source: null };
    }
    // Pick the LATEST source — the candidate isn't really available until ALL
    // constraints are satisfied (e.g., notice-period-expiry AND a manually-set
    // earliest_join_date both have to be met).
    candidates.sort((a, b) => b.date.getTime() - a.date.getTime());
    const pick = candidates[0];
    const daysOut = Math.round((pick.date.getTime() - todayUTC().getTime()) / DAY_MS);
    return { date: pick.date, daysOut, source: pick.source };
}

/** Friendly short string e.g. "Available now" / "Available in 14d (15 Mar)" / "Joined" */
export function availabilityLabel({ date, daysOut, source }) {
    if (!date) return null;
    const dStr = date.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
    if (daysOut <= 0) return "Available now";
    return `In ${daysOut}d · ${dStr}`;
}

export function availabilityTone(daysOut) {
    if (daysOut === null || daysOut === undefined) return "neutral";
    if (daysOut <= 0) return "now";
    if (daysOut <= 7) return "soon";
    if (daysOut <= 30) return "ok";
    return "far";
}
